import { useState, useEffect, useCallback } from 'react'
import { BlogService, BlogFilters } from '../services/blogService'
import { BlogPost, BlogsResponse } from '../config/api'

export type { BlogFilters } from '../services/blogService'

interface UseBlogsOptions {
  initialFilters?: BlogFilters
  initialPage?: number
}

export const useBlogs = (options: UseBlogsOptions = {}) => {
  const { initialFilters = {}, initialPage = 1 } = options
  const [blogs, setBlogs] = useState<BlogPost[]>([])
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 12,
    has_next: false,
    has_prev: false,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<BlogFilters>(initialFilters)
  const [currentPage, setCurrentPage] = useState(1)

  const blogService = BlogService.getInstance()

  const fetchBlogs = useCallback(
    async (page: number = 1, newFilters?: BlogFilters) => {
      try {
        setLoading(true)
        setError(null)

        const filtersToUse = newFilters || filters
        const response: BlogsResponse = await blogService.getBlogs(page, filtersToUse)

        setBlogs(response.blogs)
        setPagination(response.pagination)
        setCurrentPage(page)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch blogs')
      } finally {
        setLoading(false)
      }
    },
    [filters, blogService]
  )

  // Initial load
  useEffect(() => {
    fetchBlogs(initialPage, initialFilters)
  }, [])

  // Filter blogs
  const applyFilters = useCallback(
    (newFilters: BlogFilters) => {
      setFilters(newFilters)
      setCurrentPage(1)
      fetchBlogs(1, newFilters)
    },
    [fetchBlogs]
  )

  // Load more/next page
  const loadPage = useCallback(
    (page: number) => {
      if (page >= 1 && page <= pagination.total_pages) {
        fetchBlogs(page)
      }
    },
    [fetchBlogs, pagination.total_pages]
  )

  // Helper functions
  const goToNextPage = useCallback(() => {
    if (pagination.has_next) {
      loadPage(currentPage + 1)
    }
  }, [loadPage, currentPage, pagination.has_next])

  const goToPrevPage = useCallback(() => {
    if (pagination.has_prev) {
      loadPage(currentPage - 1)
    }
  }, [loadPage, currentPage, pagination.has_prev])

  const refresh = useCallback(() => {
    fetchBlogs(currentPage)
  }, [fetchBlogs, currentPage])

  return {
    blogs,
    pagination,
    loading,
    error,
    filters,
    currentPage,
    applyFilters,
    loadPage,
    goToNextPage,
    goToPrevPage,
    refresh,
  }
}
